/**
 * useWizardSourceRows — the selection planes the create wizard's steps 2–6
 * render (W12 Part B, Task B3; the thesis plane added by W13 IS2).
 *
 * The create-mode `CompositeDataPicker` is gone, so the rows the operator picks
 * from are loaded HERE, from the same firebase-core services the pickers used:
 * `TaskService` (routes of the pinned analysis), `InsightThesisService` (the
 * theses of the pinned analysis), `InsightVariableGenerationService` (accepted
 * variables of the chosen generation), the project's own variable map, and the
 * four social-proof services.
 *
 * Rules:
 * - `enabled` is `mode === 'create' && open` — edit mode keeps the picker and
 *   must not pay for a second load.
 * - Each loader runs once per key (projectId / generationId) and its result is
 *   ignored after unmount or after the key changed.
 * - A loader failure is surfaced per plane (the table's `error` prop), never as
 *   a toast and never as an empty list pretending to be the truth.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FunnelVariable } from '@segmently/schema-models/v2';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { InsightVariableGenerationService } from '@modules/firebase-core/services/InsightVariableGenerationService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import { InsightThesisService } from '@modules/firebase-core/services/InsightThesisService';
import type { SelectionRow } from './SelectionTable';
import type { SelectionPlane } from './selectionPaths';

/** Social-proof rows carry their plane: one table writes four path planes. */
export interface SocialProofRow extends SelectionRow {
  plane: Extract<SelectionPlane, 'review' | 'rating' | 'studyCase' | 'bigNumber'>;
  /**
   * HB-5: the hydrated entity behind the row. The wizard writes selection PATHS
   * and never reads it; the block-update override sends the entities themselves
   * (`sourceDataOverride` replaces the head's source data wholesale), so a row
   * the strategy does not already carry would otherwise be a silent drop.
   */
  entity?: Record<string, unknown>;
}

/**
 * W13 IS2: one thesis of the pinned analysis, as the «Insights» step shows it.
 * `name` is the canonical description (the table's first column); the rest are
 * the columns the operator judges the thesis by, plus the SCOPE that decides
 * which namespace the request narrows (ruling W13-R22).
 */
export interface InsightThesisSelectionRow extends SelectionRow {
  category: string;
  actorCount: number;
  cumulativeWeight: number;
  scope: 'global' | 'task';
  /** Task packages holding this thesis; empty for a global one. */
  taskIds: string[];
  /** «Global» or the task names — what the Scope column renders. */
  scopeLabel: string;
}

export interface WizardSourcePlane<R extends SelectionRow = SelectionRow> {
  rows: R[];
  loading: boolean;
  error: string | null;
}

export interface UseWizardSourceRowsArgs {
  projectId: string;
  /** Task routes the pinned analysis spans. `undefined` = nothing pinned yet. */
  analysisTaskIds?: string[];
  /**
   * W13 IS2: the pinned analysis whose THESES the «Insights» step selects from.
   * `undefined` (or empty) = nothing pinned, so nothing is read.
   */
  analysisId?: string;
  /** Chosen insight-variable generation (`''` = product variables only). */
  generationId: string;
  projectVariables: Record<string, FunnelVariable>;
  enabled: boolean;
}

export interface UseWizardSourceRowsResult {
  tasks: WizardSourcePlane;
  /** W13 IS2: the theses of the pinned analysis (empty while nothing is pinned). */
  insight: WizardSourcePlane<InsightThesisSelectionRow>;
  insightVariables: WizardSourcePlane;
  projectVariables: {
    rows: SelectionRow[];
    /** Variable id → the task ids it is scoped to (globals are absent). */
    taskScopedIds: Record<string, string[]>;
  };
  socialProof: WizardSourcePlane<SocialProofRow>;
}

/** Rows (or the error) a loader produced, tagged with the key they belong to. */
interface LoadedPlane<R> {
  key: string | null;
  rows: R[];
  error: string | null;
}

const EMPTY_LOADED = { key: null, rows: [], error: null } as LoadedPlane<never>;

const errorMessage = (error: unknown, fallback: string): string =>
  (error instanceof Error && error.message) || fallback;

/** Task ids a variable is scoped to (empty/absent = global). */
const taskIdsOf = (variable: FunnelVariable | undefined): string[] => {
  const ids = (variable as unknown as { taskIds?: string[] } | undefined)?.taskIds;
  return Array.isArray(ids) ? ids : [];
};

/**
 * The FunnelVariable inside a generatedVariables document. The agent writes it
 * under `data.variable`; imported/legacy documents carry a flat `variable` —
 * both are accepted (same tolerance as `InsightVariableFieldSelector`).
 */
const insightVariableOf = (entity: unknown): Record<string, unknown> => {
  const raw = entity as { data?: { variable?: unknown }; variable?: unknown };
  const candidate = (raw?.data?.variable ?? raw?.variable) as Record<string, unknown> | undefined;
  return candidate && typeof candidate === 'object' ? candidate : {};
};

export function useWizardSourceRows({
  projectId,
  analysisTaskIds,
  analysisId,
  generationId,
  projectVariables,
  enabled,
}: UseWizardSourceRowsArgs): UseWizardSourceRowsResult {
  /**
   * Each async plane holds the KEY its rows (or its error) belong to, so
   * «loading» is a synchronous comparison against the key the props ask for.
   * A boolean flag flipped inside the loader effect would read `false` for one
   * render — long enough for a consumer to apply its defaults to an empty list.
   */
  const [tasks, setTasks] = useState<LoadedPlane<{ id: string; name: string }>>(EMPTY_LOADED);
  const [insightVariables, setInsightVariables] = useState<LoadedPlane<SelectionRow>>(EMPTY_LOADED);
  const [socialProof, setSocialProof] = useState<LoadedPlane<SocialProofRow>>(EMPTY_LOADED);
  const [insightTheses, setInsightTheses] =
    useState<LoadedPlane<InsightThesisSelectionRow>>(EMPTY_LOADED);

  const pinned = Array.isArray(analysisTaskIds);
  const analysisTaskKey = (analysisTaskIds ?? []).join(',');
  const tasksNeeded = enabled && pinned;
  const insightsNeeded = enabled && Boolean(analysisId);
  const insightsLoading = insightsNeeded && insightTheses.key !== analysisId;
  const insightVariablesNeeded = enabled && Boolean(generationId);
  const tasksLoading = tasksNeeded && tasks.key !== projectId;
  const insightVariablesLoading = insightVariablesNeeded && insightVariables.key !== generationId;
  const socialProofLoading = enabled && socialProof.key !== projectId;

  // ── Tasks: one load per project, filtered to the pinned analysis's routes ──
  const taskLoadKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tasksNeeded) {
      taskLoadKeyRef.current = null;
      return undefined;
    }
    if (taskLoadKeyRef.current === projectId) return undefined;
    taskLoadKeyRef.current = projectId;

    let cancelled = false;
    new TaskService(projectId, taskConverter).list()
      .then((loaded) => {
        if (cancelled) return;
        setTasks({
          key: projectId,
          rows: loaded
            .filter((task): task is typeof task & { id: string } => Boolean(task.id))
            .map((task) => ({ id: task.id, name: task.name || task.id })),
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setTasks({ key: projectId, rows: [], error: errorMessage(error, 'Failed to load tasks') });
      });
    return () => { cancelled = true; };
  }, [tasksNeeded, projectId]);

  /**
   * ── W13 IS2: the theses of the pinned analysis, one load per pin ──────────
   *
   * Re-pinning re-reads: the two analyses are different id namespaces, so a
   * stale row list would offer ids the new analysis does not contain (which the
   * server refuses by name, AC-W13-21). The dialog resets the SELECTION to «all»
   * on the same key — the rows here are only what is offered.
   */
  const insightLoadKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!insightsNeeded || !analysisId) {
      insightLoadKeyRef.current = null;
      return undefined;
    }
    if (insightLoadKeyRef.current === analysisId) return undefined;
    insightLoadKeyRef.current = analysisId;

    let cancelled = false;
    new InsightThesisService(projectId).listTheses(analysisId)
      .then(({ rows, taskNames }) => {
        if (cancelled) return;
        setInsightTheses({
          key: analysisId,
          rows: rows.map((row) => ({
            id: row.id,
            // The canonical description IS the row: it is the sentence the
            // prompt will carry, so the operator judges exactly what is sent.
            name: row.canonicalDescription,
            category: row.category,
            actorCount: row.actorCount,
            cumulativeWeight: row.cumulativeWeight,
            scope: row.scope,
            taskIds: row.taskIds,
            scopeLabel: row.scope === 'global'
              ? 'Global'
              : row.taskIds.map((taskId) => taskNames[taskId] ?? taskId).join(' · '),
          })),
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setInsightTheses({
          key: analysisId,
          rows: [],
          error: errorMessage(error, 'Failed to load the insight theses'),
        });
      });
    return () => { cancelled = true; };
  }, [insightsNeeded, projectId, analysisId]);

  // ── Insight-derived variables: one load per generation, accepted only ──
  const generationLoadKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!insightVariablesNeeded) {
      generationLoadKeyRef.current = null;
      return undefined;
    }
    if (generationLoadKeyRef.current === generationId) return undefined;
    generationLoadKeyRef.current = generationId;

    let cancelled = false;
    new InsightVariableGenerationService(projectId).listVariables(generationId)
      .then((entities) => {
        if (cancelled) return;
        setInsightVariables({
          key: generationId,
          rows: entities
            .filter((entity) => entity.accepted === true)
            .map((entity) => {
              const variable = insightVariableOf(entity);
              const sourceQuestion = (variable.collectionContract as
                { sourceQuestion?: { questionText?: string } } | undefined)?.sourceQuestion;
              return {
                id: String(variable.id ?? entity.id),
                name: String(variable.name ?? variable.id ?? entity.id),
                detail: String(sourceQuestion?.questionText ?? variable.description ?? ''),
              };
            })
            .filter((row) => Boolean(row.id)),
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setInsightVariables({
          key: generationId,
          rows: [],
          error: errorMessage(error, 'Failed to load insight-derived variables'),
        });
      });
    return () => { cancelled = true; };
  }, [insightVariablesNeeded, projectId, generationId]);

  // ── Social proof: the four kinds in one plane-tagged list ──
  const socialProofLoadKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) {
      socialProofLoadKeyRef.current = null;
      return undefined;
    }
    if (socialProofLoadKeyRef.current === projectId) return undefined;
    socialProofLoadKeyRef.current = projectId;

    let cancelled = false;
    Promise.all([
      new ReviewService(projectId).list({ limit: 100 }),
      new RatingService(projectId).list({ limit: 100 }),
      new StudyCaseService(projectId).list({ limit: 100 }),
      new BigNumberService(projectId).list({ limit: 100 }),
    ])
      .then(([reviews, ratings, studyCases, bigNumbers]) => {
        if (cancelled) return;
        const rows: SocialProofRow[] = [
          ...reviews.map((review) => ({
            plane: 'review' as const,
            id: review.id,
            name: review.nickname || 'Review',
            detail: `Review · ${review.rating}★`,
            entity: review as unknown as Record<string, unknown>,
          })),
          ...ratings.map((rating) => ({
            plane: 'rating' as const,
            id: rating.id,
            name: `${rating.rating}★ · ${rating.ratingCount}`,
            detail: `Rating · ${rating.platform}`,
            entity: rating as unknown as Record<string, unknown>,
          })),
          ...studyCases.map((studyCase) => ({
            plane: 'studyCase' as const,
            id: studyCase.id,
            name: studyCase.featureName || 'Study case',
            detail: `Study case · ${studyCase.primaryBenefit ?? ''}`.trim(),
            entity: studyCase as unknown as Record<string, unknown>,
          })),
          ...bigNumbers.map((bigNumber) => ({
            plane: 'bigNumber' as const,
            id: bigNumber.id,
            name: bigNumber.description || 'Big number',
            detail: `Big number · ${bigNumber.amount}`,
            entity: bigNumber as unknown as Record<string, unknown>,
          })),
        ];
        setSocialProof({ key: projectId, rows, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setSocialProof({
          key: projectId,
          rows: [],
          error: errorMessage(error, 'Failed to load social proof'),
        });
      });
    return () => { cancelled = true; };
  }, [enabled, projectId]);

  // ── Derived rows ──
  const taskRows = useMemo<SelectionRow[]>(() => {
    if (!pinned) return [];
    const allowed = new Set(analysisTaskIds ?? []);
    return tasks.rows
      .filter((task) => allowed.has(task.id))
      .map((task) => {
        const scoped = Object.values(projectVariables)
          .filter((variable) => taskIdsOf(variable).includes(task.id)).length;
        return {
          id: task.id,
          name: task.name,
          detail: scoped > 0
            ? `${scoped} question${scoped === 1 ? '' : 's'} specific to this goal`
            : 'Global questions only',
        };
      });
    // `analysisTaskKey` keeps the memo honest when the array identity changes
    // but its content does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, analysisTaskKey, tasks.rows, projectVariables]);

  const projectVariableRows = useMemo<SelectionRow[]>(() => {
    const taskNames = new Map(tasks.rows.map((task) => [task.id, task.name]));
    return Object.values(projectVariables)
      .filter((variable): variable is FunnelVariable => Boolean(variable?.id))
      .map((variable) => {
        const scopedTo = taskIdsOf(variable);
        // Brief's detail vocabulary: «global» | «goal: <task>» | «required».
        const detail = scopedTo.length > 0
          ? `goal: ${scopedTo.map((taskId) => taskNames.get(taskId) ?? taskId).join(', ')}`
          : (variable.required ? 'required' : 'global');
        return { id: variable.id, name: variable.name || variable.id, detail };
      });
  }, [projectVariables, tasks.rows]);

  const taskScopedIds = useMemo<Record<string, string[]>>(() => {
    const scoped: Record<string, string[]> = {};
    for (const variable of Object.values(projectVariables)) {
      const ids = taskIdsOf(variable);
      if (variable?.id && ids.length > 0) scoped[variable.id] = ids;
    }
    return scoped;
  }, [projectVariables]);

  const tasksPlane = useMemo<WizardSourcePlane>(
    () => ({ rows: taskRows, loading: tasksLoading, error: tasksNeeded ? tasks.error : null }),
    [taskRows, tasksLoading, tasksNeeded, tasks.error],
  );
  const insightPlane = useMemo<WizardSourcePlane<InsightThesisSelectionRow>>(() => ({
    rows: insightsNeeded ? insightTheses.rows : [],
    loading: insightsLoading,
    error: insightsNeeded ? insightTheses.error : null,
  }), [insightsNeeded, insightTheses, insightsLoading]);
  const insightVariablesPlane = useMemo<WizardSourcePlane>(() => ({
    rows: insightVariablesNeeded ? insightVariables.rows : [],
    loading: insightVariablesLoading,
    error: insightVariablesNeeded ? insightVariables.error : null,
  }), [insightVariablesNeeded, insightVariables, insightVariablesLoading]);
  const socialProofPlane = useMemo<WizardSourcePlane<SocialProofRow>>(() => ({
    rows: enabled ? socialProof.rows : [],
    loading: socialProofLoading,
    error: enabled ? socialProof.error : null,
  }), [enabled, socialProof, socialProofLoading]);
  const projectVariablesPlane = useMemo(
    () => ({ rows: projectVariableRows, taskScopedIds }),
    [projectVariableRows, taskScopedIds],
  );

  return {
    tasks: tasksPlane,
    insight: insightPlane,
    insightVariables: insightVariablesPlane,
    projectVariables: projectVariablesPlane,
    socialProof: socialProofPlane,
  };
}
